/** How automatic / incremental runs behave (manual ↓ Download always available). */
export type SyncJobType = 'upload-only' | 'two-way';

export const DEFAULT_SYNC_JOB_TYPE: SyncJobType = 'upload-only';

/** One sync job: local source folder → a specific host PC + shared folder (+ optional subfolder). Stored only on this PWA/browser. */
export interface SyncProfile {
  id: string;
  label: string;
  /** Display name of the local directory (File System Access API). */
  localDirName: string;
  syncType: SyncJobType;
  /** Which host PC to dial (signaling + P2P), same as My Drives. */
  pcId: string;
  ownerUserId: string;
  pcName: string;
  /** Host share root path key (server/desktop string). */
  hostSharePath: string;
  hostShareName: string;
  /** Destination under that share ("" = share root). */
  hostRelDir: string;
}

const KEY = 'hc_sync_profiles_v2';

function storageKey(userId: string): string {
  return `${KEY}_${userId}`;
}

function normalizeProfile(raw: Partial<SyncProfile>): SyncProfile | null {
  if (!raw.id || !raw.localDirName) return null;
  if (!raw.pcId || !raw.ownerUserId || !raw.hostSharePath) return null;
  return {
    id: raw.id,
    label: raw.label || raw.localDirName,
    localDirName: raw.localDirName,
    pcId: raw.pcId,
    ownerUserId: raw.ownerUserId,
    pcName: raw.pcName || 'Home PC',
    hostSharePath: raw.hostSharePath,
    hostShareName: raw.hostShareName || raw.hostSharePath,
    hostRelDir: (raw.hostRelDir ?? '').trim().replace(/\\/g, '/').replace(/\/$/, ''),
    syncType: raw.syncType === 'two-way' ? 'two-way' : DEFAULT_SYNC_JOB_TYPE,
  };
}

export function syncTypeLabel(type: SyncJobType): string {
  return type === 'two-way' ? 'Two-way sync' : 'Upload only';
}

/** Migrate v1 entries keyed per pcId into v2 list (best effort). */
function migrateV1(userId: string): SyncProfile[] {
  const out: SyncProfile[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k?.startsWith('hc_sync_profiles_v1_')) continue;
      const pcId = k.slice('hc_sync_profiles_v1_'.length);
      const raw = window.localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Partial<SyncProfile>[];
      if (!Array.isArray(parsed)) continue;
      for (const p of parsed) {
        const n = normalizeProfile({
          ...p,
          pcId: p.pcId || pcId,
          ownerUserId: p.ownerUserId || '',
          pcName: p.pcName || 'Home PC',
          hostShareName: p.hostShareName || p.hostSharePath || '',
        });
        if (n && n.ownerUserId) out.push(n);
      }
    }
    if (out.length) saveSyncProfiles(userId, out);
  } catch {
    /* ignore */
  }
  return out;
}

export function loadSyncProfiles(userId: string): SyncProfile[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) {
      return migrateV1(userId);
    }
    const parsed = JSON.parse(raw) as Partial<SyncProfile>[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeProfile).filter((p): p is SyncProfile => p !== null);
  } catch {
    return [];
  }
}

export function saveSyncProfiles(userId: string, profiles: SyncProfile[]): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(profiles));
  } catch {
    /* private mode */
  }
}

export function newProfileId(): string {
  return `sync-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
